import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import HighlightText from '../components/HighlightText';

const { FiClock, FiDollarSign, FiStar, FiShield, FiUsers, FiTrendingUp, FiZap, FiChevronDown, FiChevronRight, FiSend, FiCheckCircle } = FiIcons;

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Become a Home Tutor in Lucknow | MyHomeTutors',
  description: 'Join MyHomeTutors as a verified home tutor in Lucknow. Earn well, teach flexibly, and grow your tutoring career with 500+ active tutors.',
  url: 'https://myhometutors.org/become-a-tutor',
  publisher: {
    '@type': 'Organization',
    name: 'MyHomeTutors',
    telephone: '+919415905584',
  },
};

const steps = [
  { num: '01', title: 'Register Online', desc: 'Fill the simple application form on Taskade with your details.' },
  { num: '02', title: 'Profile Verification', desc: 'Our team verifies your qualifications and conducts a background check.' },
  { num: '03', title: 'Get Your Profile Listed', desc: 'Your verified profile goes live and becomes visible to students near you.' },
  { num: '04', title: 'Start Teaching & Earning', desc: 'Get matched with students, schedule your classes, and start earning.' },
];

const perks = [
  { icon: FiDollarSign, title: 'Competitive Earnings', desc: 'Earn ₹300–₹1500/hour based on your subject, class level, and experience.', color: 'bg-green-50 text-green-600' },
  { icon: FiClock, title: 'Flexible Schedule', desc: 'You decide when to teach. Morning, evening, weekends — all up to you.', color: 'bg-blue-50 text-blue-600' },
  { icon: FiStar, title: 'Build Your Reputation', desc: 'Get genuine student reviews that boost your profile and attract more students.', color: 'bg-yellow-50 text-yellow-600' },
  { icon: FiShield, title: 'Verified Badge', desc: 'Our verification badge builds instant trust with parents and students.', color: 'bg-purple-50 text-purple-600' },
  { icon: FiUsers, title: 'Large Student Base', desc: 'Access thousands of active students searching for tutors across Lucknow.', color: 'bg-pink-50 text-pink-600' },
  { icon: FiTrendingUp, title: 'Career Growth', desc: 'Build a full-time or part-time tutoring career with ongoing support from our team.', color: 'bg-orange-50 text-orange-600' },
];

const faqs = [
  { q: 'Who can apply to become a tutor?', a: 'Anyone with a relevant educational background and passion for teaching. This includes graduates, postgraduates, B.Ed holders, engineers, and subject matter experts.' },
  { q: 'Is there any registration fee?', a: 'No. Registering as a tutor on MyHomeTutors is completely free. We earn only when we successfully match you with a student.' },
  { q: 'How quickly will I get students?', a: 'After profile verification (usually 24-48 hours), you can start receiving student inquiries. Most tutors get their first student within a week.' },
  { q: 'What subjects can I teach?', a: 'We accept tutors for all subjects — Maths, Science, English, Commerce, Hindi, Coding, IIT-JEE, NEET, and more.' },
  { q: 'Do I need to travel to students?', a: 'Yes, primarily we provide home tutors. However, if you prefer online tutoring, we can accommodate that too.' },
];

const testimonials = [
  { name: 'Priya Sharma', role: 'Math Tutor, 3 Years', img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=80&q=80', text: 'Joining MyHomeTutors was the best decision. I now earn more than my office job while working just 4 hours a day.' },
  { name: 'Rahul Verma', role: 'Physics & Chemistry, 5 Years', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=80&q=80', text: 'The platform is very professional. Students are serious, parents are cooperative, and payments are always on time.' },
  { name: 'Anjali Singh', role: 'English Tutor, 2 Years', img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=80&q=80', text: 'I love the flexibility. I set my own hours, choose my students, and the MyHomeTutors team is always supportive.' },
];

const FAQItem = ({ faq, isOpen, onClick }) => (
  <div className={`border rounded-2xl overflow-hidden transition-all mb-3 ${isOpen ? 'border-red-200 bg-red-50/30' : 'border-gray-100 bg-white'}`}>
    <button className="w-full text-left px-6 py-4 flex items-center justify-between gap-4" onClick={onClick}>
      <span className="font-extrabold text-slate-800 text-sm">{faq.q}</span>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isOpen ? 'bg-primary text-white' : 'bg-red-50 text-primary'}`}>
        <FiChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
    </button>
    <div className={`px-6 overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-48 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
      <p className="text-gray-600 font-medium text-sm leading-relaxed">{faq.a}</p>
    </div>
  </div>
);

const BecomeTutorFullPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Become a Home Tutor in Lucknow | MyHomeTutors</title>
        <meta name="description" content="Join MyHomeTutors as a verified home tutor in Lucknow. Earn well, teach flexibly, and grow your tutoring career with 500+ active tutors." />
        <link rel="canonical" href="https://myhometutors.org/become-a-tutor/" />
        <meta property="og:title" content="Become a Home Tutor in Lucknow | MyHomeTutors" />
        <meta property="og:description" content="Join MyHomeTutors as a verified home tutor in Lucknow. Earn well, teach flexibly, and grow your tutoring career." />
        <meta property="og:url" content="https://myhometutors.org/become-a-tutor" />
        <meta property="og:type" content="website" />
      </Helmet>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#fff5f5] via-[#fff8f8] to-[#ffeaea] py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#fd6c6b 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="space-y-7">
              <div className="inline-flex items-center gap-2 bg-white text-primary text-xs font-bold px-4 py-2 rounded-full border border-red-100 shadow-sm uppercase tracking-widest">
                <FiZap className="w-3.5 h-3.5" /> Now Hiring Tutors in Lucknow
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-slate-900 leading-[1.15] tracking-tight">
                Turn Your Knowledge<br /> into <HighlightText>Income</HighlightText>
              </h1>
              <p className="text-lg text-gray-600 font-medium leading-relaxed max-w-lg">
                Join 500+ verified tutors earning ₹15,000–₹50,000/month. Teach from home, set your own hours, and make a real difference in students' lives.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: FiDollarSign, label: '₹300–₹1500/hr', sub: 'Competitive Pay' },
                  { icon: FiUsers, label: '10,000+ Students', sub: 'Active on Platform' },
                  { icon: FiClock, label: 'Flexible Hours', sub: 'You Set the Schedule' },
                  { icon: FiShield, label: 'Free Registration', sub: 'No Hidden Charges' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/80 px-4 py-3 rounded-2xl border border-white shadow-sm">
                    <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-extrabold text-slate-900 text-sm">{item.label}</p>
                      <p className="text-xs text-gray-500 font-medium">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="https://myhometutors.taskade.site/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-xl font-extrabold hover:bg-primary-hover transition-all shadow-lg hover:-translate-y-0.5 transform tracking-wide"
              >
                <FiSend className="w-4 h-4" /> Apply Now — It's Free
              </a>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img src="https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=700&q=80" alt="Become a tutor" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-4 z-10">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <FiCheckCircle className="text-green-600 w-6 h-6" />
                </div>
                <div>
                  <p className="text-xl font-extrabold text-slate-900">500+</p>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Active Tutors</p>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 z-10">
                <div className="flex items-center gap-2">
                  {[...Array(5)].map((_, i) => <FiStar key={i} className="w-4 h-4 fill-current text-yellow-400" />)}
                </div>
                <p className="text-sm font-extrabold text-slate-900 mt-1">4.9 / 5 Rating</p>
                <p className="text-xs text-gray-500 font-medium">By Students</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#fdf8f4]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-3">
              How to <HighlightText>Get Started</HighlightText>
            </h2>
            <p className="text-gray-500 font-medium">Four simple steps to start your tutoring journey</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all group">
                <div className="text-4xl font-extrabold text-red-100 mb-4 group-hover:text-red-200 transition">{step.num}</div>
                <h3 className="font-extrabold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500 font-medium leading-relaxed">{step.desc}</p>
                {i < steps.length - 1 && (
                  <FiChevronRight className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 text-primary hidden lg:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-3">
              Why Join <HighlightText>MyHomeTutors?</HighlightText>
            </h2>
            <p className="text-gray-500 font-medium max-w-xl mx-auto">
              We provide everything you need to build a successful, sustainable tutoring career.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {perks.map((perk, i) => (
              <div key={i} className="flex gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-red-100 hover:shadow-md transition-all group">
                <div className={`w-12 h-12 rounded-2xl ${perk.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <perk.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 mb-1">{perk.title}</h3>
                  <p className="text-sm text-gray-500 font-medium leading-relaxed">{perk.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#fdf8f4]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">
              Tutors Love <HighlightText>Working With Us</HighlightText>
            </h2>
            <p className="text-gray-500 font-medium">Hear from tutors who are already growing with MyHomeTutors</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover border-2 border-red-100" />
                  <div>
                    <p className="font-extrabold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500 font-medium">{t.role}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, j) => <FiStar key={j} className="w-3.5 h-3.5 fill-current text-yellow-400" />)}
                </div>
                <p className="text-sm text-gray-600 font-medium leading-relaxed">"{t.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">
              Common <HighlightText>Questions</HighlightText>
            </h2>
            <p className="text-gray-500 font-medium">Everything you need to know before applying</p>
          </div>
          {faqs.map((faq, i) => (
            <FAQItem key={i} faq={faq} isOpen={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default BecomeTutorFullPage;